import { useQuery } from '@apollo/client';
import { getSubdomainParameter } from '@carlito/browser-utils';
import { useMemo } from 'react';
import { GET_AVAILABLE_LANGUAGES } from '../infrastructure/graphql/getAvailableLanguages.gql';
import {
  GetAvailableLanguages,
  GetAvailableLanguagesVariables,
} from '../infrastructure/graphql/types/GetAvailableLanguages';
import { ALL_LOCALES, DEFAULT_HOTEL_AVAILABLE_LANGUAGES } from '@carlito/intl';
import { useGetHotelBySubdomain } from './useGetHotelBySubdomain';

interface QueryOptions {
  pollInterval?: number;
  hotelId?: string;
}

export function useGetAvailableLanguages(options: QueryOptions = {}) {
  const hotelSubdomain = getSubdomainParameter('app');

  const { hotel } = useGetHotelBySubdomain(
    { subdomain: hotelSubdomain },
    {
      skip: Boolean(options.hotelId) || !hotelSubdomain,
    }
  );

  const hotelId = options.hotelId ?? hotel?.id ?? '';
  const { data, loading, ...rest } = useQuery<
    GetAvailableLanguages,
    GetAvailableLanguagesVariables
  >(GET_AVAILABLE_LANGUAGES, {
    ...options,
    variables: {
      hotelId,
    },
    skip: !hotelId,
  });

  const { availableLanguages, supportedLocales } = useMemo(() => {
    let availableLanguages = ALL_LOCALES;

    const hotelAvailableLanguages = data?.hotel?.availableLanguages?.length
      ? data.hotel.availableLanguages
      : DEFAULT_HOTEL_AVAILABLE_LANGUAGES;

    availableLanguages = ALL_LOCALES.filter((language) =>
      hotelAvailableLanguages.includes(language.value)
    );
    return {
      availableLanguages: availableLanguages,
      supportedLocales: availableLanguages.map((el) => el.value),
    };
  }, [data?.hotel?.availableLanguages]);

  const defaultLocale = supportedLocales.includes('en') ? 'en' : supportedLocales[0] ?? 'en';
  return { availableLanguages, supportedLocales, loading, ...rest, defaultLocale };
}
